import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  Line,
  YAxis,
  XAxis,
  Area,
  Label,
  ComposedChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
} from 'recharts';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrDollarSign from 'client/components/SrPriceMarketFitSunSpot/SrDollarSign';
import SrPriceTooltip from 'client/components/SrPriceMarketFitSunSpot/SrPriceTooltip';
import SrDayOnMarketTick from 'client/components/SrPriceMarketFitSunSpot/SrDayOnMarketTick';
import SrTodayDot from 'client/components/SrPriceMarketFitSunSpot/SrTodayDot';
import SrTodayTooltip from 'client/components/SrPriceMarketFitSunSpot/SrTodayTooltip';

import './SrPriceMarketFitSunSpotChart.less';

const axisStyle = {
  fontFamily: 'Greycliff',
  fill: 'rgba(0, 0, 0, 0.4)',
};

const labelStyle = {
  fontFamily: 'Greycliff',
  fill: '#1C1C1C',
};

function findADaysOnMarketPoints(arr, value) {
  let lowerValue = null;
  let higherValue = null;

  for (let i = 0; i < arr.length; i += 1) {
    if (arr[i].payload.daysOnMarket <= value) {
      lowerValue = arr[i];
    }
    if (arr[i].payload.daysOnMarket >= value) {
      higherValue = arr[i];
      break;
    }
  }

  return [lowerValue, higherValue];
}

function getCXDaysOnMarket(p1, p2, priceChange) {
  const xDiff = p2.x - p1.x;
  const daysDiff = priceChange.daysOnMarket - p1.payload.daysOnMarket;
  const cx = (daysDiff * xDiff) / 7 + p1.x;

  return cx;
}

function getCY(x1, y1, x2, y2, x) {
  const y = y1 + ((y2 - y1) / (x2 - x1) || 0) * (x - x1);

  return y;
}

const Prices = ({ priceChanges, formattedGraphicalItems, graphIndex }) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;

  const dollarSigns = priceChanges.map((priceChange) => {
    const points = findADaysOnMarketPoints(leadsGraphPoints, priceChange.daysOnMarket);

    const cx = getCXDaysOnMarket(points[0], points[1], priceChange);
    const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

    return <SrDollarSign key={priceChange.daysOnMarket} cy={cy} cx={cx} />;
  });

  return dollarSigns;
};

const PriceToolTips = ({ priceChanges, formattedGraphicalItems, graphIndex }) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;

  const dollarSigns = priceChanges.map((priceChange) => {
    const points = findADaysOnMarketPoints(leadsGraphPoints, priceChange.daysOnMarket);

    const cx = getCXDaysOnMarket(points[0], points[1], priceChange);
    const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

    return (
      <SrPriceTooltip key={priceChange.daysOnMarket} cy={cy} cx={cx} price={priceChange.price} />
    );
  });

  return dollarSigns;
};

const TodayDot = ({ listingStat, formattedGraphicalItems, graphIndex }) => {
  const leadsGraphPoints = formattedGraphicalItems[graphIndex].props.points;
  const todayMoment = moment().format('YYYY-MM-DD');
  const todayPoint = leadsGraphPoints.find((point) => point.payload.date === todayMoment);

  if (!todayPoint) {
    return null;
  }

  const daysOnMarket = todayPoint.payload.daysOnMarket;

  const points = findADaysOnMarketPoints(leadsGraphPoints, daysOnMarket);
  const cx = getCXDaysOnMarket(points[0], points[1], { daysOnMarket });
  const cy = getCY(points[0].x, points[0].y, points[1].x, points[1].y, cx);

  return (
    <>
      <SrTodayDot cx={cx} cy={cy} />
      <SrTodayTooltip cx={cx} cy={cy} listingStat={listingStat} />
    </>
  );
};

const Calculation = ({ yAxisMap, onLoad }) => {
  if (yAxisMap[0]) {
    onLoad(yAxisMap[0].niceTicks[yAxisMap[0].niceTicks.length - 1]);
  }

  return null;
};

const SrPriceMarketFitSunSpotChart = ({ listing, listingStat, weeklyLeads, priceChanges }) => {
  const [maxY, setMaxY] = useState(null);

  const showSunSpot = !!(listing.avgLeadsComposition && listing.avgLeadsCompositionDate);

  const data =
    maxY === null
      ? weeklyLeads
      : weeklyLeads.map((weeklyLead) => {
          return {
            ...weeklyLead,
            hot: maxY - weeklyLead.cold - weeklyLead.sunSpot,
          };
        });

  const graphIndex = showSunSpot ? (maxY === null ? 2 : 3) : 0;

  return (
    <SrBox bgColor="white" padding="24px 20px 20px 10px">
      {showSunSpot && (
        <SrFlex justify="between" align="end" paddingRight="26px" marginBottom="24px">
          <div>
            <span styleName="chartTitle">SunSpot Chart</span>
          </div>
          <div>
            <span styleName="colorText red">Red</span>
            <span styleName="text">= less than avg leads</span>
            <span styleName="colorText green">Green</span>
            <span styleName="text">= more than avg leads</span>
            <span styleName="colorText yellow">SunSpot</span>
            <span styleName="text">= avg leads</span>
          </div>
        </SrFlex>
      )}
      <ResponsiveContainer width="100%" height={450}>
        <ComposedChart data={data} margin={{ top: 10, bottom: 50, right: 26 }}>
          <defs>
            <linearGradient id="hot" x1="0" y1="0" x2="0" y2="1">
              <stop offset="10%" stopColor="#00A238" stopOpacity={1} />
              <stop offset="100%" stopColor="#00A238" stopOpacity={0.2} />
            </linearGradient>
          </defs>
          <defs>
            <linearGradient id="cold" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FCDCE0" stopOpacity={1} />
              <stop offset="130%" stopColor="#EE4459" stopOpacity={1} />
            </linearGradient>
          </defs>
          <YAxis axisLine={false} tickLine={false} style={axisStyle}>
            <Label angle={-90} position="insideCenterLeft" style={labelStyle}>
              Leads (Rolling Past 7 Days)
            </Label>
          </YAxis>
          <XAxis
            tickLine={false}
            axisLine={false}
            dataKey="daysOnMarket"
            style={axisStyle}
            padding={{ bottom: 10 }}
            tick={<SrDayOnMarketTick data={weeklyLeads} />}
          >
            <Label offset={30} position="bottom" style={labelStyle}>
              Days on Market
            </Label>
          </XAxis>
          {showSunSpot && (
            <>
              <Area
                type="monotone"
                dataKey="cold"
                stackId="1"
                fillOpacity={1}
                stroke={0}
                fill="url(#cold)"
              />
              <Area
                type="monotone"
                dataKey="sunSpot"
                stackId="1"
                stroke={0}
                fillOpacity={1}
                fill="#FFF5CC"
              />
              <Area
                type="monotone"
                dataKey="hot"
                stackId="1"
                stroke={0}
                fillOpacity={1}
                fill="url(#hot)"
              />
              <Customized
                component={(props) => {
                  return (
                    <Calculation
                      {...props}
                      listing={listing}
                      onLoad={(maxY) => {
                        setMaxY(maxY);
                      }}
                    />
                  );
                }}
              />
            </>
          )}
          <Line
            type="monotone"
            dataKey="leads"
            stroke="#FFCD00"
            strokeWidth={5}
            fill="#FFCD00"
            filter="drop-shadow(0px 5px 5px rgba(0, 0, 0, 0.13))"
            dot={false}
          />
          <CartesianGrid vertical={false} stroke="rgba(0, 0, 0, 0.08)" fill={false} />
          <Customized
            component={(props) => {
              return <Prices {...props} graphIndex={graphIndex} priceChanges={priceChanges} />;
            }}
          />
          <Customized
            component={(props) => {
              return (
                <PriceToolTips {...props} graphIndex={graphIndex} priceChanges={priceChanges} />
              );
            }}
          />
          <Customized
            component={(props) => {
              return <TodayDot {...props} listingStat={listingStat} graphIndex={graphIndex} />;
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </SrBox>
  );
};

SrPriceMarketFitSunSpotChart.propTypes = {
  weeklyLeads: PropTypes.array.isRequired,
  priceChanges: PropTypes.array.isRequired,
};

SrPriceMarketFitSunSpotChart.displayName = 'SrPriceMarketFitSunSpotChart';

export default SrPriceMarketFitSunSpotChart;
